<template>
    <div class="password-container">
        <el-card class="card">
            <div class="title">【修改密码】</div>
            <el-form :model="passwordForm" :rules="rules" ref="passwordForm" label-width="120px" class="input-width">
                <el-form-item label="旧密码" prop="oldPassword">
                    <el-input v-model="passwordForm.oldPassword" type="password" placeholder="请输入旧密码"
                        autocomplete="new-password" show-password></el-input>
                </el-form-item>
                <el-form-item label="新密码" prop="newPassword">
                    <div class="new-password-row">
                        <el-input v-model="passwordForm.newPassword" type="password" placeholder="6-32位新密码"
                            autocomplete="new-password" show-password></el-input>
                        <el-link type="primary" :underline="false" @click="generateRandomPassword">生成随机密码</el-link>
                    </div>
                </el-form-item>
                <el-form-item label="确认新密码" prop="confirmPassword">
                    <el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请再次输入新密码"
                        autocomplete="new-password" show-password></el-input>
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" @click="submitForm" :loading="submitting">确认修改</el-button>
                    <el-button @click="resetForm">重置</el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <!-- 修改成功提示弹窗：强提示妥善保管密码，并提供一键复制 -->
        <el-dialog title="密码修改成功" :visible.sync="successDialogVisible" width="460px" :close-on-click-modal="false"
            :close-on-press-escape="false" :show-close="false">
            <div class="success-tip">
                <el-alert type="warning" :closable="false" show-icon
                    title="请务必妥善保管新密码！" description="密码修改后立即生效，平台不提供密码找回功能。若密码丢失，将无法登录后台。建议复制后存入密码管理工具。">
                </el-alert>
                <div class="new-password-display">
                    <span class="label">新密码：</span>
                    <code class="password-text">{{ passwordForm.newPassword }}</code>
                    <el-button type="primary" size="small" icon="el-icon-document-copy" @click="copyNewPassword">复制密码</el-button>
                </div>
            </div>
            <div slot="footer">
                <el-button type="primary" @click="confirmSaved">我已妥善保存</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { updatePassword } from '@/api/user'

export default {
    name: 'Password',
    data() {
        const validateConfirmPassword = (rule, value, callback) => {
            if (value !== this.passwordForm.newPassword) {
                callback(new Error('两次输入的密码不一致'));
            } else {
                callback();
            }
        };
        return {
            passwordForm: {
                oldPassword: '',
                newPassword: '',
                confirmPassword: ''
            },
            rules: {
                oldPassword: [
                    { required: true, message: '请输入旧密码', trigger: 'blur' }
                ],
                newPassword: [
                    { required: true, message: '请输入新密码', trigger: 'blur' },
                    { min: 6, max: 32, message: '新密码长度需在6-32位之间', trigger: 'blur' }
                ],
                confirmPassword: [
                    { required: true, message: '请再次输入新密码', trigger: 'blur' },
                    { validator: validateConfirmPassword, trigger: 'blur' }
                ]
            },
            submitting: false,
            successDialogVisible: false// 修改成功提示弹窗是否显示
        };
    },
    methods: {
        // 生成12位随机密码（大小写字母+数字），自动填写新密码与确认密码输入框
        generateRandomPassword() {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            const randomValues = new Uint32Array(12);
            if (window.crypto && window.crypto.getRandomValues) {
                window.crypto.getRandomValues(randomValues);
            } else {
                for (let i = 0; i < randomValues.length; i++) {
                    randomValues[i] = Math.floor(Math.random() * chars.length);
                }
            }
            let password = '';
            for (let i = 0; i < randomValues.length; i++) {
                password += chars[randomValues[i] % chars.length];
            }
            this.passwordForm.newPassword = password;
            this.passwordForm.confirmPassword = password;
            this.$refs.passwordForm.clearValidate(['newPassword', 'confirmPassword']);
            this.$message.success('已生成随机密码，请妥善保管');
        },
        submitForm() {
            this.$refs.passwordForm.validate(async (valid) => {
                if (!valid) return;
                this.submitting = true;
                try {
                    const response = await updatePassword({
                        oldPassword: this.passwordForm.oldPassword,
                        newPassword: this.passwordForm.newPassword
                    });
                    if (response.code === 0) {
                        // 弹出强提示弹窗，先不重置表单，保留新密码供复制；确认保存后再清空
                        this.successDialogVisible = true;
                    } else {
                        this.$message.error('密码修改失败: ' + response.message);
                    }
                } catch (error) {
                    this.$message.error('密码修改失败: ' + (error.message || error));
                } finally {
                    this.submitting = false;
                }
            });
        },
        // 一键复制新密码（优先 Clipboard API，非安全上下文降级为 execCommand）
        copyNewPassword() {
            const password = this.passwordForm.newPassword;
            const onSuccess = () => this.$message.success('密码已复制，请妥善保管');
            const onFail = () => this.$message.error('复制失败，请手动选择密码复制');
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(password).then(onSuccess).catch(() => {
                    this.copyByExecCommand(password) ? onSuccess() : onFail();
                });
            } else {
                this.copyByExecCommand(password) ? onSuccess() : onFail();
            }
        },
        copyByExecCommand(text) {
            try {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                const ok = document.execCommand('copy');
                document.body.removeChild(textarea);
                return ok;
            } catch (e) {
                return false;
            }
        },
        // 用户确认已妥善保存后关闭弹窗并清空表单
        confirmSaved() {
            this.successDialogVisible = false;
            this.resetForm();
        },
        resetForm() {
            this.$refs.passwordForm.resetFields();
        }
    }
};
</script>

<style lang="scss" scoped>
.password-container {
    padding: 20px;
}

.card {
    min-height: 500px;
}

.title {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 20px;
}

.input-width {
    max-width: 560px;
}

.new-password-row {
    display: flex;
    align-items: center;

    .el-input {
        flex: 1;
    }

    .el-link {
        margin-left: 10px;
        flex-shrink: 0;
    }
}

/* 修改成功提示弹窗 */
.success-tip {
    .el-alert {
        align-items: flex-start;
        margin-bottom: 20px;
    }
}

.new-password-display {
    display: flex;
    align-items: center;

    .label {
        flex-shrink: 0;
        color: #606266;
    }

    .password-text {
        flex: 1;
        margin: 0 10px;
        padding: 6px 10px;
        background: #f5f7fa;
        border: 1px dashed #c0c4cc;
        border-radius: 4px;
        font-size: 15px;
        letter-spacing: 1px;
        user-select: all;
        word-break: break-all;
    }
}

@media (max-width: 768px) {
    .password-container {
        padding: 10px;
    }

    .el-form-item__label {
        float: none;
        display: block;
        text-align: left;
        padding: 0 0 10px;
    }

    .el-form-item__content {
        margin-left: 0 !important;
    }
}
</style>
